// WorkflowTable — AG Grid SSRM with tree data.
//
// Data flow:
//   Root rows  → WorkflowsGraphqlDataSource.fetchRootRows()
//                → controlTowerWorkflows(limit, page) — server-side pagination
//   Child rows → WorkflowsGraphqlDataSource.fetchChildRows()
//                → non-Jira: task cache (populated during root fetch, no extra call)
//                → Jira: GetJiraChildWorkItems

import React, { createElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApolloClient, ApolloClient } from "@apollo/client";
import { AgGrid, Grid } from "@jsq/ds/core";
import { ControlTowerContext } from "#utils/context";
import { useControlTowerContext } from "../../ControlTowerContext";
import { SavedViewPanel } from "../../TicketsTable/components/SavedViewPanel";
import { useWorkflowViewManager } from "../hooks/useWorkflowViewManager";
import { isTaskStatusBlocked } from "../taskStatusConfig";
import { getWorkflowColumnDefs } from "./columnDefs";
import { WorkflowBulkActionsPanel } from "./components/WorkflowBulkActionsPanel";
import { WorkflowExportPanel } from "./components/WorkflowExportPanel";
import { getGridOptions } from "./gridOptions";
import * as styles from "./styles_vanilla.css";
import { WorkflowsGraphqlDataSource } from "./workflowsGraphqlDataSource";
import type { WorkflowFilterMode } from "../index";
import type {
  WorkflowColumnFilterOptions,
  WorkflowInstanceRow,
  WorkflowTableRow,
} from "../types";
import type { NormalizedCacheObject } from "@apollo/client";

type WorkflowTableProps = {
  showExpanded: boolean;
  filterMode: WorkflowFilterMode;
  filterOptions?: WorkflowColumnFilterOptions;
  filterOptionsLoading?: boolean;
  initialFilterModel?: AgGrid.FilterModel | null;
  hasInitialUrlFilters?: boolean;
  onHasActiveFiltersChange?: (has: boolean) => void;
  clearFiltersTrigger?: number;
  onFilterChanged?: (filterModel: AgGrid.FilterModel | null) => void;
  onWorkflowClick?: (workflowId: string, instanceRow?: WorkflowInstanceRow) => void;
  onTaskClick?: (workflowId: string, taskId: string, instanceRow?: WorkflowInstanceRow) => void;
  onViewerWorkflowUserIdResolved?: (id: string) => void;
};

type WorkflowColumnDefs = ReturnType<typeof useWorkflowViewManager>["columnDefs"];

const getDefaultHiddenColumnState = (columnDefs: WorkflowColumnDefs) => {
  if (!columnDefs) return [];

  return columnDefs.flatMap((colDef) =>
    "field" in colDef && colDef.field && colDef.hide
      ? [{ colId: colDef.field, hide: true }]
      : [],
  );
};

const applyDefaultHiddenColumns = (
  api: AgGrid.GridApi<WorkflowTableRow>,
  columnDefs: WorkflowColumnDefs,
): void => {
  const state = getDefaultHiddenColumnState(columnDefs);
  if (state.length === 0) return;

  api.applyColumnState({ state });
};

export const WorkflowTable: React.FC<WorkflowTableProps> = ({
  showExpanded,
  filterMode,
  filterOptions,
  filterOptionsLoading = false,
  initialFilterModel,
  hasInitialUrlFilters = false,
  onHasActiveFiltersChange,
  clearFiltersTrigger,
  onFilterChanged,
  onWorkflowClick,
  onTaskClick,
  onViewerWorkflowUserIdResolved,
}) => {
  const apolloClient = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const context = useControlTowerContext();
  const isCanopy = context === ControlTowerContext.CANOPY;
  const isGPX = context === ControlTowerContext.GPX;

  const [gridApi, setGridApi] = useState<AgGrid.GridApi<WorkflowTableRow> | null>(null);
  const gridApiRef = useRef<AgGrid.GridApi<WorkflowTableRow> | null>(null);
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRestoringFilterRef = useRef(false);

  useEffect(() => {
    return () => {
      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    };
  }, []);

  // ── Datasource ─────────────────────────────────────────────────────────────
  // Created once on mount with the initial values; all subsequent changes flow
  // through the setter effects below. Using a lazy ref avoids useMemo with a
  // suppressed exhaustive-deps rule — the instance is stable for the lifetime
  // of this component and AG Grid only accepts a datasource once (onGridReady).
  const datasourceRef = useRef<WorkflowsGraphqlDataSource | null>(null);
  if (!datasourceRef.current) {
    datasourceRef.current = new WorkflowsGraphqlDataSource({
      apolloClient,
      showExpanded,
      filterMode,
      onViewerWorkflowUserIdResolved,
    });
  }

  useEffect(() => {
    datasourceRef.current?.setOnViewerWorkflowUserIdResolved(onViewerWorkflowUserIdResolved);
  }, [onViewerWorkflowUserIdResolved]);

  useEffect(() => {
    datasourceRef.current?.setShowExpanded(showExpanded);
    if (gridApiRef.current) {
      gridApiRef.current.refreshServerSide({ purge: true });
      gridApiRef.current.paginationGoToFirstPage();
    }
  }, [showExpanded]);

  useEffect(() => {
    datasourceRef.current?.setExternalFilters(filterMode);
    if (gridApiRef.current) {
      gridApiRef.current.refreshServerSide({ purge: true });
      gridApiRef.current.paginationGoToFirstPage();
    }
  }, [filterMode]);

  // ── Column defs ─────────────────────────────────────────────────────────────
  const getColumnDefsCallback = useCallback(
    () => getWorkflowColumnDefs(showExpanded, filterOptions, context),
    [showExpanded, filterOptions, context],
  );

  const {
    columnDefs,
    shouldShowGrid,
    saveViewLoading,
    handleSavedViewClicked,
    savedConfiguration,
  } = useWorkflowViewManager({
    getColumnDefs: getColumnDefsCallback,
    gridApi,
    skip: filterOptionsLoading || isGPX,
    hasInitialUrlFilters,
  });

  // ── Context task click — enriches with instanceRow from datasource cache ───
  // The grid context onTaskClick only receives (workflowId, taskId).
  // We wrap it here to look up the instanceRow so the drawer gets the workflow.
  const contextTaskClick = useCallback(
    (workflowId: string, taskId: string) => {
      const instanceRow = datasourceRef.current?.getWorkflowInstance(workflowId);
      onTaskClick?.(workflowId, taskId, instanceRow);
    },
    [onTaskClick],
  );

  // ── Grid ready ──────────────────────────────────────────────────────────────
  const handleGridReady = useCallback(
    (event: AgGrid.GridReadyEvent<WorkflowTableRow>) => {
      gridApiRef.current = event.api;
      setGridApi(event.api);

      if (datasourceRef.current) {
        event.api.setGridOption("serverSideDatasource", datasourceRef.current);
      }

      applyDefaultHiddenColumns(event.api, columnDefs);

      if (initialFilterModel && Object.keys(initialFilterModel).length > 0) {
        isRestoringFilterRef.current = true;
        event.api.setFilterModel(initialFilterModel);
      }

      onHasActiveFiltersChange?.(event.api.isAnyFilterPresent());
    },
    [columnDefs, initialFilterModel, onHasActiveFiltersChange],
  );

  useEffect(() => {
    if (!gridApi || !columnDefs) return;
    applyDefaultHiddenColumns(gridApi, columnDefs);
  }, [columnDefs, gridApi]);

  // ── Filter changed ──────────────────────────────────────────────────────────
  const handleFilterChanged = useCallback(() => {
    onHasActiveFiltersChange?.(gridApiRef.current?.isAnyFilterPresent() ?? false);

    const isRestoring = isRestoringFilterRef.current;
    isRestoringFilterRef.current = false;

    if (!isRestoring && onFilterChanged) {
      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
      filterDebounceRef.current = setTimeout(() => {
        const model = gridApiRef.current?.getFilterModel() ?? null;
        onFilterChanged(!model || Object.keys(model).length === 0 ? null : model);
      }, 300);
    }

    gridApiRef.current?.paginationGoToFirstPage();
    gridApiRef.current?.refreshServerSide({ purge: true });
  }, [onFilterChanged, onHasActiveFiltersChange]);

  // ── Sort changed ──────────────────────────────────────────────────────────
  // Sorting is server-side: reset to page 1 and refetch so the newly-sorted
  // result starts from the top, regardless of which page the user was on.
  const handleSortChanged = useCallback(() => {
    gridApiRef.current?.paginationGoToFirstPage();
    gridApiRef.current?.refreshServerSide({ purge: true });
  }, []);

  // ── Filter restore on showExpanded change ───────────────────────────────────
  useEffect(() => {
    if (
      !gridApiRef.current ||
      !initialFilterModel ||
      Object.keys(initialFilterModel).length === 0
    )
      return;
    isRestoringFilterRef.current = true;
    gridApiRef.current.setFilterModel(initialFilterModel);
  }, [showExpanded, initialFilterModel]);

  // ── Clear filters trigger ───────────────────────────────────────────────────
  const prevClearTriggerRef = useRef(clearFiltersTrigger ?? 0);
  useEffect(() => {
    const current = clearFiltersTrigger ?? 0;
    if (!gridApi || current === prevClearTriggerRef.current) return;
    prevClearTriggerRef.current = current;
    gridApi.setFilterModel(null);
    gridApi.applyColumnState({ defaultState: { sort: null } });
    gridApi.paginationGoToFirstPage();
    gridApi.refreshServerSide({ purge: true });
  }, [gridApi, clearFiltersTrigger]);

  // ── Row click ───────────────────────────────────────────────────────────────
  const handleRowClicked = useCallback(
    (event: AgGrid.RowClickedEvent<WorkflowTableRow>) => {
      const data = event.data;
      if (!data || !onWorkflowClick) return;

      const nativeTarget = event.event?.target as HTMLElement | undefined;
      if (nativeTarget?.closest("[data-step-group]")) return;
      if (data.isJiraPlaceholder || data.isJiraLoading) return;

      if (data.isParentRow && data.workflowId) {
        const instanceRow = datasourceRef.current?.getWorkflowInstance(data.workflowId);
        onWorkflowClick(data.workflowId, instanceRow);
        return;
      }

      // Blocked tasks (BLOCKED / NOT_STARTED) are non-interactive — same rule as
      // the drawer's TaskList component.
      if (!data.isParentRow && isTaskStatusBlocked(data.status ?? "")) return;

      if (!data.isParentRow && data.path.length >= 1) {
        const parentWorkflowId = data.path[0];
        const instanceRow = datasourceRef.current?.getWorkflowInstance(parentWorkflowId);
        if (onTaskClick && data.rawTaskId) {
          onTaskClick(parentWorkflowId, data.rawTaskId, instanceRow);
        } else {
          onWorkflowClick(parentWorkflowId, instanceRow);
        }
      }
    },
    [onWorkflowClick, onTaskClick],
  );

  // ── Framework components ────────────────────────────────────────────────────
  const frameworkComponents = useMemo(
    () => ({
      bulkActionsPanel: (props: AgGrid.IToolPanelParams) =>
        createElement(WorkflowBulkActionsPanel, { ...props }),
      exportPanel: (props: AgGrid.IToolPanelParams) =>
        createElement(WorkflowExportPanel, { ...props, datasource: datasourceRef.current! }),
      savedViewPanel: (props: AgGrid.IToolPanelParams) =>
        createElement(SavedViewPanel, {
          ...props,
          saveViewLoading,
          handleSavedViewClicked,
          savedConfiguration,
        }),
      workflowLoadingOverlay: () =>
        createElement(
          "div",
          { className: styles.loadingOverlay },
          createElement("span", { className: "ag-icon ag-icon-loading" }),
          createElement("span", {}, "Loading"),
        ),
    }),
    [saveViewLoading, handleSavedViewClicked, savedConfiguration],
  );

  // ── Grid options ────────────────────────────────────────────────────────────
  const gridOpts = useMemo(() => {
    const baseOptions = getGridOptions(showExpanded, contextTaskClick, isGPX)!;
    const allPanels =
      typeof baseOptions.sideBar === "object" &&
      baseOptions.sideBar !== null &&
      "toolPanels" in baseOptions.sideBar
        ? (baseOptions.sideBar.toolPanels ?? [])
        : [];

    return {
      ...baseOptions,
      loadingOverlayComponent: "workflowLoadingOverlay",
      sideBar: {
        position: "right" as const,
        toolPanels: allPanels.filter((panel: AgGrid.ToolPanelDef | string) => {
          const id = (panel as AgGrid.ToolPanelDef).id;
          if (!isCanopy && (id === "bulkActions" || id === "savedView")) return false;
          return true;
        }),
      },
      onGridReady: handleGridReady,
    };
  }, [showExpanded, contextTaskClick, isGPX, isCanopy, handleGridReady]);

  const savedColumnOrder = savedConfiguration?.columnOrder?.orderedColIds;
  const hasSavedColumnOrder = Boolean(savedColumnOrder?.length);
  const columnOrderCanopy = { orderedColIds: ["workflowId", "ag-Grid-AutoColumn"] };
  const initialState =
    !hasSavedColumnOrder && !showExpanded && !isGPX
      ? { columnOrder: columnOrderCanopy }
      : undefined;

  useEffect(() => {
    if (!gridApi) return;

    if (savedColumnOrder?.length) {
      gridApi.applyColumnState({
        state: savedColumnOrder.map((colId) => ({ colId })),
        applyOrder: true,
      });
      return;
    }

    if (isGPX || showExpanded) return;
    gridApi.applyColumnState({
      state: [{ colId: "workflowId" }, { colId: "ag-Grid-AutoColumn" }],
      applyOrder: true,
    });
  }, [columnDefs, gridApi, isGPX, savedColumnOrder, showExpanded]);

  return (
    <div className={styles.gridWrapper}>
      {shouldShowGrid && columnDefs && (
        <Grid<WorkflowTableRow>
          columnDefs={columnDefs}
          gridOptions={gridOpts}
          onRowClicked={handleRowClicked}
          onFilterChanged={handleFilterChanged}
          onSortChanged={handleSortChanged}
          components={frameworkComponents}
          suppressColumnVirtualisation={false}
          suppressRowVirtualisation={false}
          initialState={initialState}
        />
      )}
    </div>
  );
};

export default WorkflowTable;
